// Fixed catalog of components + actions for tool configuration UIs.
//
// Tools declare a `configSchema` (JSON Schema, source of truth) and
// a `configUI` spec in their `toolConfig.json`. The configUI binds
// to schema fields via `path` (dot notation). Components read title /
// description / required-ness / format from the schema — they never
// redeclare them.
//
// See Common/ToolConfigCatalog.md for the tool-authoring reference.
// Keep that doc in sync when you change this file.

import React, { createContext, useContext, useState } from 'react'
import { defineCatalog } from '@json-render/core'
import { schema as jrSchema } from '@json-render/react/schema'
import { defineRegistry } from '@json-render/react'
import { z } from 'zod'
import { resolveSchema, isRequired, getAtPath } from './schema.js'

// ---------------------------------------------------------------------
// Form state context. Carries both the live values AND the schema,
// so inputs can look up title/description/required/format by path.
// ---------------------------------------------------------------------
const FormContext = createContext({
  values: {},
  schema: null,
  setValue: () => {},
  save: async () => {},
})

export const FormProvider = FormContext.Provider
export const useFormContext = () => useContext(FormContext)

// ---------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------
export const catalog = defineCatalog(jrSchema, {
  components: {
    Section: {
      description: 'Titled vertical group of fields.',
      props: z.object({
        title: z.string().optional(),
      }),
    },
    Heading: {
      description: 'Heading text.',
      props: z.object({
        text: z.string(),
        level: z.enum(['h1', 'h2', 'h3']).optional(),
      }),
    },
    Help: {
      description: 'Secondary help / description text.',
      props: z.object({
        text: z.string(),
      }),
    },
    TextInput: {
      description: 'Single-line text input, bound to a schema path.',
      props: z.object({
        path: z.string(),
      }),
    },
    SecretInput: {
      description: 'Password-masked input, bound to a schema path.',
      props: z.object({
        path: z.string(),
      }),
    },
    SaveButton: {
      description: 'Persists the state model to Firestore.',
      props: z.object({
        label: z.string().optional(),
      }),
    },
  },
  actions: {
    save_config: { description: 'Persist the current state model.' },
  },
})

// ---------------------------------------------------------------------
// Component implementations
// ---------------------------------------------------------------------
const Section = ({ props, children }) => (
  <fieldset style={{
    border: '1px solid #ddd',
    padding: '1rem',
    marginBottom: '1rem',
    borderRadius: 4,
  }}>
    {props.title && (
      <legend style={{ padding: '0 .5rem', fontWeight: 600 }}>{props.title}</legend>
    )}
    {children}
  </fieldset>
)

const Heading = ({ props }) => {
  const Tag = props.level || 'h3'
  return <Tag style={{ margin: '.25rem 0' }}>{props.text}</Tag>
}

const Help = ({ props }) => (
  <div style={{
    fontSize: '.875rem',
    color: '#666',
    marginBottom: '.5rem',
    lineHeight: 1.4,
  }}>
    {props.text}
  </div>
)

// Derives everything an input needs (label/help/required/format/value)
// from the schema at `path` plus the current values.
const useFieldMeta = (path) => {
  const { values, schema, setValue } = useFormContext()
  const node = resolveSchema(schema, path)
  const required = isRequired(schema, path)
  const raw = getAtPath(values, path)
  const value = raw == null ? '' : raw
  return {
    value,
    setValue: (v) => setValue(path, v),
    title: (node && node.title) || path,
    description: node && node.description,
    format: node && node.format,
    examples: (node && node.examples) || [],
    required,
    missing: required && (raw == null || raw === ''),
  }
}

const FieldShell = ({ meta, children }) => (
  <div style={{ marginBottom: '.75rem' }}>
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: '.875rem', marginBottom: '.25rem' }}>
        {meta.title}
        {meta.required && (
          <span style={{ color: '#c00', marginLeft: 4 }} aria-label="required">*</span>
        )}
      </div>
      {children}
    </label>
    {meta.description && (
      <div style={{ fontSize: '.75rem', color: '#666', marginTop: '.25rem' }}>
        {meta.description}
      </div>
    )}
  </div>
)

const inputStyle = (missing, mono) => ({
  width: '100%',
  padding: '.5rem',
  border: `1px solid ${missing ? '#c00' : '#ccc'}`,
  borderRadius: 4,
  ...(mono ? { fontFamily: 'monospace' } : {}),
})

const TextInput = ({ props }) => {
  const m = useFieldMeta(props.path)
  return (
    <FieldShell meta={m}>
      <input
        type="text"
        value={m.value}
        placeholder={m.examples[0] || ''}
        onChange={e => m.setValue(e.target.value)}
        style={inputStyle(m.missing, false)}
      />
    </FieldShell>
  )
}

const SecretInput = ({ props }) => {
  const m = useFieldMeta(props.path)
  return (
    <FieldShell meta={m}>
      <input
        type="password"
        value={m.value}
        placeholder={m.examples[0] || ''}
        onChange={e => m.setValue(e.target.value)}
        autoComplete="off"
        style={inputStyle(m.missing, true)}
      />
    </FieldShell>
  )
}

const SaveButton = ({ props }) => {
  const { save } = useFormContext()
  const [busy, setBusy] = useState(false)
  const [ok, setOk] = useState(false)
  const [err, setErr] = useState(null)
  const onClick = async () => {
    setBusy(true); setOk(false); setErr(null)
    try {
      await save()
      setOk(true)
      setTimeout(() => setOk(false), 1500)
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        style={{
          background: '#0066cc',
          color: 'white',
          border: 'none',
          padding: '.5rem 1rem',
          borderRadius: 4,
          cursor: busy ? 'default' : 'pointer',
        }}
      >
        {busy ? 'Saving…' : (ok ? 'Saved ✓' : (props.label || 'Save'))}
      </button>
      {err && (
        <div style={{ color: '#c00', fontSize: '.875rem', marginTop: '.5rem' }}>
          {err}
        </div>
      )}
    </div>
  )
}

export const { registry } = defineRegistry(catalog, {
  components: {
    Section,
    Heading,
    Help,
    TextInput,
    SecretInput,
    SaveButton,
  },
})
